import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search, AlertTriangle, Calendar, MapPin, Shield,
  ChevronDown, ChevronRight, CheckCircle2, Circle, XCircle,
} from "lucide-react";
import SectionCard from "../components/SectionCard";
import ProximosPasos from "@/components/ProximosPasos";
import MapaViaje from "@/components/MapaViaje";
import EstimadorDrawer from "@/components/EstimadorDrawer";
import BarChart from "../components/charts/BarChart";
import AreaChart from "../components/charts/AreaChart";
import { PACIENTES_MOCK } from "../mocks/data";
import { formatNum } from "../lib/constants";
import { cn } from "../lib/utils";
import { buscarPaciente } from "../lib/pacienteSearch";
import { detectarProximosPasos, calcularProgreso } from "@/lib/detectarPasos";

// Colores por macro-etapa del pipeline
const ETAPA_COLORS = {
  "Acceso": "#7C3AED",
  "Atención": "#3B82F6",
  "Diagnóstica": "#14B8A6",
  "Tratamiento": "#F59E0B",
  "Financiero": "#EC4899",
  "Seguimiento": "#6366F1",
};

const ETAPAS_ORDERED = ["Acceso", "Atención", "Diagnóstica", "Tratamiento", "Financiero", "Seguimiento"];

// Mapeo de ámbitos de eventos a macro-etapas
function clasificarEvento(ambito) {
  const a = ambito?.toLowerCase() || "";
  if (a.includes("cancelación") || a.includes("cancelacion")) return "Acceso";
  if (a.includes("contacto email") || a.includes("contacto whatsapp")) return "Seguimiento";
  if (a.includes("agenda") || a.includes("búsqueda") || a.includes("reserva")) return "Acceso";
  if (a.includes("consulta") || a.includes("ambulatorio") || a.includes("telemedicina")) return "Atención";
  if (a.includes("lab") || a.includes("imagen") || a.includes("examen") || a.includes("radiograf") || a.includes("ecograf")) return "Diagnóstica";
  if (a.includes("farmacia") || a.includes("cirugía") || a.includes("medicamento") || a.includes("procedimiento")) return "Tratamiento";
  if (a.includes("pago") || a.includes("bono") || a.includes("financ") || a.includes("copago")) return "Financiero";
  if (a.includes("seguimiento") || a.includes("control") || a.includes("encuesta") || a.includes("crm")) return "Seguimiento";
  return "Atención"; // default
}

// Detectar tipo de viaje según eventos
function detectarTipoViaje(eventos) {
  const ambitos = eventos.map((e) => e.ambito?.toLowerCase() || "");
  if (ambitos.some((a) => a.includes("oncolog") || a.includes("quimio") || a.includes("radio")))
    return { tipo: "Oncológico", color: "#EF4444", icono: "🎗️" };
  if (ambitos.some((a) => a.includes("diálisis") || a.includes("hemodiálisis") || a.includes("peritoneal")))
    return { tipo: "Diálisis", color: "#D946EF", icono: "💉" };
  if (ambitos.some((a) => a.includes("cirugía") || a.includes("pabellón") || a.includes("quirúrgico") || a.includes("hospitaliz")))
    return { tipo: "Quirúrgico / Hospitalario", color: "#F59E0B", icono: "🏥" };
  if (ambitos.some((a) => a.includes("urgencia")))
    return { tipo: "Urgencia + Ambulatorio", color: "#F97316", icono: "🚑" };
  return { tipo: "Ambulatorio", color: "#8B5CF6", icono: "🩺" };
}

// Detectar etapas completadas
function detectarEtapas(eventos) {
  const etapasVistas = new Set(eventos.map((e) => clasificarEvento(e.ambito)));
  return ETAPAS_ORDERED.map((etapa) => ({
    etapa,
    completada: etapasVistas.has(etapa),
    color: ETAPA_COLORS[etapa],
  }));
}

// Icono para tipo de evento especial
function getEventIcon(ambito) {
  const a = ambito?.toLowerCase() || "";
  if (a.includes("cancelación") || a.includes("cancelacion")) return "❌";
  if (a.includes("contacto email")) return "📧";
  if (a.includes("contacto whatsapp")) return "💬";
  return null;
}

// Estilo especial para eventos
function getEventStyle(ambito) {
  const a = ambito?.toLowerCase() || "";
  if (a.includes("cancelación") || a.includes("cancelacion")) return "text-red-600 font-semibold";
  if (a.includes("contacto email")) return "text-violet-600";
  if (a.includes("contacto whatsapp")) return "text-emerald-600";
  return "text-slate-700";
}

// Kanban cell component — compact list of events for a stage+year
function KanbanCell({ eventos, etapa }) {
  if (!eventos || eventos.length === 0) {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  return (
    <div className="space-y-1.5">
      {eventos.map((e, i) => {
        const icon = getEventIcon(e.ambito);
        const style = getEventStyle(e.ambito);
        const fechaCorta = e.fecha ? e.fecha.substring(5) : ""; // MM-DD
        return (
          <div key={i} className={cn("text-xs leading-tight", style)} title={`${e.fecha} · ${e.descripcion}`}>
            <span className="text-[10px] text-slate-400 tabular-nums mr-1">{fechaCorta}</span>
            {icon && <span className="mr-0.5">{icon}</span>}
            {e.descripcion.length > 35 ? e.descripcion.slice(0, 33) + "…" : e.descripcion}
          </div>
        );
      })}
    </div>
  );
}

export default function Paciente() {
  const [searchParams] = useSearchParams();
  const [rut, setRut] = useState(searchParams.get("rut") || "");
  const [paciente, setPaciente] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [dataSource, setDataSource] = useState(null); // "mock" | "api"
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [showEstimador, setShowEstimador] = useState(false);

  // Cargar paciente desde query param (no cargar default)
  useEffect(() => {
    const queryRut = searchParams.get("rut");
    if (queryRut) {
      setRut(queryRut);
      doSearch(queryRut);
    }
  }, [searchParams]);

  const doSearch = async (rutBuscar) => {
    setLoading(true);
    setNotFound(false);
    setApiError(null);
    setDataSource(null);
    try {
      const { paciente: result, source, error } = await buscarPaciente(rutBuscar);
      if (result) {
        setPaciente(result);
        setNotFound(false);
        setDataSource(source);
      } else {
        setPaciente(null);
        setNotFound(true);
        if (error) setApiError(error);
      }
    } catch (err) {
      setPaciente(null);
      setNotFound(true);
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    if (rut.trim()) doSearch(rut.trim());
  };

  const handleSelectEjemplo = (e) => {
    const selectedRut = e.target.value;
    if (selectedRut) {
      setRut(selectedRut);
      doSearch(selectedRut);
    }
  };

  const data = paciente;
  const tipoViaje = data ? detectarTipoViaje(data.eventos) : null;
  const etapasStatus = data ? detectarEtapas(data.eventos) : [];

  // Build Kanban data: year → etapa → events[]
  const kanbanData = useMemo(() => {
    if (!data) return {};
    const result = {};
    data.eventos.forEach((e) => {
      const anio = e.fecha?.substring(0, 4) || "Sin fecha";
      const etapa = clasificarEvento(e.ambito);
      if (!result[anio]) result[anio] = {};
      if (!result[anio][etapa]) result[anio][etapa] = [];
      result[anio][etapa].push(e);
    });
    // Sort events within each cell by date descending
    for (const anio of Object.keys(result)) {
      for (const etapa of Object.keys(result[anio])) {
        result[anio][etapa].sort((a, b) => b.fecha.localeCompare(a.fecha));
      }
    }
    return result;
  }, [data]);

  // Detect cycles: a new cycle starts when there's a new "Acceso" event after a gap
  const ciclosPorAnio = useMemo(() => {
    if (!data) return {};
    const result = {};
    for (const [anio, yearData] of Object.entries(kanbanData)) {
      const accesos = (yearData["Acceso"] || []).length;
      const atenciones = (yearData["Atención"] || []).length;
      // Approximate cycles: max of accesos or atenciones (each visit is roughly a cycle)
      result[anio] = Math.max(accesos, atenciones, 1);
    }
    return result;
  }, [kanbanData]);

  const totalCiclos = Object.values(ciclosPorAnio).reduce((s, c) => s + c, 0);

  const anios = Object.keys(kanbanData).sort((a, b) => b.localeCompare(a));

  // Expand most recent year by default
  useEffect(() => {
    if (anios.length > 0 && expandedYears.size === 0) {
      setExpandedYears(new Set([anios[0]]));
    }
  }, [anios]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleYear = (anio) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(anio)) next.delete(anio);
      else next.add(anio);
      return next;
    });
  };

  // Convenios — usar datos de API si disponibles, sino mock
  const convenios = data?.convenios && data.convenios.length > 0
    ? data.convenios
    : data?.rut === "13705199-0"
      ? [
          { convenio: "PUC - Colmena Egeo", clase: "ISAPRE", vigencia: "Indefinido" },
          { convenio: "RED - Vespucio 100 (Ambulatorio)", clase: "ISAPRE", vigencia: "Indefinido" },
          { convenio: "Funcionario Red Salud UC", clase: "INTERNO", vigencia: "Indefinido" },
          { convenio: "Salud Integral Colaboradores UC", clase: "INTERNO", vigencia: "Indefinido" },
        ]
      : [{ convenio: data?.prevision || "Sin información", clase: "—", vigencia: "—" }];

  // Detect next steps for the patient
  const pasos = data ? detectarProximosPasos(data.eventos, data.quiebres) : [];
  const progreso = calcularProgreso(pasos);

  return (
    <div className="space-y-6" data-testid="paciente-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Viaje Individual del Paciente</h1>
        <p className="text-sm text-slate-500 mt-1">Timeline por RUT — vista Kanban año × macro-etapa</p>
      </div>

      {/* Buscador + Selector de ejemplo */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 rounded-xl border border-slate-200 px-4 h-11 focus-within:ring-2 focus-within:ring-violet-200 focus-within:border-violet-400 transition">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="input-rut" value={rut} onChange={(e) => setRut(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleBuscar()}
            placeholder="Ej: 12345678-9" className="flex-1 outline-none text-sm placeholder:text-slate-400" />
        </div>
        <button data-testid="search-rut-btn" onClick={handleBuscar}
          className="h-11 px-5 rounded-xl bg-gradient-to-r from-violet-600 to-blue-500 text-white text-sm font-semibold hover:from-violet-700 hover:to-blue-600 shadow-md transition">
          Buscar
        </button>
        <select onChange={handleSelectEjemplo} value="" className="h-11 rounded-xl border border-slate-200 px-3 text-sm">
          <option value="">Pacientes de ejemplo</option>
          {Object.entries(PACIENTES_MOCK).map(([r, p]) => (
            <option key={r} value={r}>{p.nombre} — {r} ({p.total_eventos} eventos)</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-6 text-center">
          <div className="w-10 h-10 border-4 border-violet-300 border-t-violet-600 rounded-full animate-spin mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900">Buscando paciente...</h3>
          <p className="text-sm text-slate-600 mt-1">Consultando Redshift en tiempo real (PRM, DG, Modulab, IMED)</p>
        </div>
      )}

      {!loading && notFound && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <XCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900">Paciente no encontrado</h3>
          <p className="text-sm text-slate-600 mt-1">No se encontró el RUT &quot;{rut}&quot; en los datos disponibles.</p>
          {apiError && (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-left">
              <p className="text-xs text-rose-700">{apiError}</p>
            </div>
          )}
        </div>
      )}

      {!loading && dataSource && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
          dataSource === "api"
            ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
            : "bg-slate-50 border border-slate-200 text-slate-600"
        }`}>
          <span>{dataSource === "api" ? "✓" : "📋"}</span>
          <span>
            {dataSource === "api"
              ? `Datos reales desde Redshift — ${data?.total_eventos || 0} eventos encontrados`
              : "Paciente de ejemplo (datos pre-cargados)"
            }
            {data?.errores_api && data.errores_api.length > 0 && (
              <span className="text-amber-600 ml-2">⚠️ {data.errores_api.length} fuente(s) con error</span>
            )}
          </span>
        </div>
      )}

      {data && (
        <>
          {/* Info paciente + tipo de viaje */}
          <div className="rounded-2xl p-6 relative overflow-hidden text-white"
               style={{ background: "linear-gradient(135deg,#4c1d95,#6d28d9,#3b82f6)" }}>
            <div className="flex flex-wrap gap-6 items-center justify-between">
              <div>
                <div className="text-xs font-medium text-violet-200 uppercase tracking-wider">Paciente</div>
                <h2 className="text-2xl font-bold mt-1">{data.nombre}</h2>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-violet-100">
                  <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> RUT {data.rut}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {data.edad} años</span>
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {data.centro_habitual}</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/15 border border-white/25">
                  <span>{tipoViaje.icono}</span>
                  <span className="text-xs font-semibold">{tipoViaje.tipo}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-violet-200 uppercase tracking-wider">Total eventos</div>
                <div className="text-5xl font-bold tracking-tight">{formatNum(data.total_eventos)}</div>
              </div>
            </div>
          </div>

          {/* Mapa visual del viaje */}
          <MapaViaje
            etapasStatus={etapasStatus}
            proximoPaso={pasos.find((p) => p.estado === "urgente" || p.estado === "pendiente")}
            eventos={data.eventos}
            pasos={pasos}
            tipoViaje={tipoViaje?.tipo}
            paciente={data}
            onEstimarCostos={() => setShowEstimador(true)}
          />

          {/* Convenios reales */}
          <SectionCard title="Convenios y Planes" subtitle="Convenios activos del paciente (fuente: adm_convenios)" testid="convenios-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {convenios.map((c, i) => {
                const clase = c.clase || c.tipo || "—";
                const nombre = c.convenio || c.nombre || "Sin información";
                const vigencia = c.vigencia || (c.vigente ? "Vigente" : "—");
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition">
                    <div className={cn(
                      "h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold",
                      clase === "ISAPRE" ? "bg-violet-500" : clase === "INTERNO" ? "bg-teal-500" : clase === "GES" ? "bg-rose-500" : "bg-slate-400"
                    )}>{clase.charAt(0)}</div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">{nombre}</div>
                      <div className="text-xs text-slate-500">{clase} · Vigencia: {vigencia}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Próximos Pasos */}
          {pasos.length > 0 && (
            <ProximosPasos pasos={pasos} progreso={progreso} />
          )}

          {/* Quiebres detectados — antes del Kanban */}
          {data.quiebres?.length > 0 && (
            <SectionCard title="Quiebres detectados" testid="quiebres-section">
              <div className="space-y-3">
                {data.quiebres.map((q, i) => {
                  const colorMap = { alta: "border-rose-200 bg-rose-50", media: "border-amber-200 bg-amber-50", baja: "border-blue-200 bg-blue-50" };
                  const textMap = { alta: "text-rose-700", media: "text-amber-700", baja: "text-blue-700" };
                  return (
                    <div key={i} className={cn("rounded-xl border p-4 flex items-start gap-3", colorMap[q.severidad])}>
                      <AlertTriangle className={cn("w-5 h-5 shrink-0 mt-0.5", textMap[q.severidad])} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={cn("text-[10px] font-bold uppercase tracking-wider", textMap[q.severidad])}>Severidad {q.severidad}</span>
                          <span className="text-xs text-slate-500">· {q.fecha}</span>
                        </div>
                        <p className="text-sm text-slate-800 mt-1 font-medium">{q.descripcion}</p>
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 bg-white border border-violet-200 rounded-lg px-3 py-1">
                          → Acción: {q.accion}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title="Eventos por macro-etapa" testid="chart-etapas">
              <BarChart
                categories={Object.keys(ETAPA_COLORS)}
                series={[{ name: "Eventos", data: Object.keys(ETAPA_COLORS).map((etapa) =>
                  data.eventos.filter((e) => clasificarEvento(e.ambito) === etapa).length
                )}]}
                colors={Object.values(ETAPA_COLORS)}
                distributed height={300}
              />
            </SectionCard>

            <SectionCard title="Actividad mensual" subtitle="Últimos 6 meses" testid="chart-actividad">
              <AreaChart
                categories={data.actividad_mensual.map((a) => a.mes)}
                series={[{ name: "Eventos", data: data.actividad_mensual.map((a) => a.eventos) }]}
                colors={["#7C3AED"]} height={300}
              />
            </SectionCard>
          </div>

          {/* Kanban: Año × Macro-Etapa */}
          <SectionCard title="Kanban — Año × Etapa" subtitle={`${anios.length} años con actividad · ~${totalCiclos} ciclos de viaje · Clic en año para expandir/colapsar`} testid="kanban-view">
            {/* Header row with stage names */}
            <div className="hidden md:grid grid-cols-6 gap-px mb-2">
              {ETAPAS_ORDERED.map((etapa) => (
                <div key={etapa} className="text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 py-1.5 rounded-t-lg"
                       style={{ borderTop: `3px solid ${ETAPA_COLORS[etapa]}` }}>
                    {etapa}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              {anios.map((anio) => {
                const isExpanded = expandedYears.has(anio);
                const yearData = kanbanData[anio] || {};
                const totalEventsYear = Object.values(yearData).reduce((sum, arr) => sum + arr.length, 0);

                return (
                  <div key={anio} className="rounded-xl border border-slate-200 overflow-hidden">
                    {/* Year header — clickable accordion */}
                    <button onClick={() => toggleYear(anio)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition bg-slate-50/50">
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <span className="text-sm font-bold text-slate-900">{anio}</span>
                        <span className="text-xs text-slate-500">{totalEventsYear} eventos · ~{ciclosPorAnio[anio] || 1} ciclos</span>
                      </div>
                      {/* Mini dots showing which stages have activity */}
                      <div className="flex gap-1">
                        {ETAPAS_ORDERED.map((etapa) => (
                          <span key={etapa}
                            className={cn("w-2 h-2 rounded-full", yearData[etapa]?.length ? "" : "opacity-20")}
                            style={{ backgroundColor: ETAPA_COLORS[etapa] }}
                            title={`${etapa}: ${yearData[etapa]?.length || 0} eventos`} />
                        ))}
                      </div>
                    </button>

                    {/* Kanban grid — 6 columns */}
                    {isExpanded && (
                      <div className="border-t border-slate-100">
                        {/* Desktop: grid */}
                        <div className="hidden md:grid grid-cols-6 gap-px bg-slate-100">
                          {ETAPAS_ORDERED.map((etapa) => (
                            <div key={etapa} className="bg-white p-3 min-h-[80px]"
                                 style={{ borderTop: `2px solid ${ETAPA_COLORS[etapa]}` }}>
                              <KanbanCell eventos={yearData[etapa]} etapa={etapa} />
                            </div>
                          ))}
                        </div>
                        {/* Mobile: stacked */}
                        <div className="md:hidden divide-y divide-slate-100">
                          {ETAPAS_ORDERED.map((etapa) => {
                            const eventos = yearData[etapa];
                            if (!eventos || eventos.length === 0) return null;
                            return (
                              <div key={etapa} className="p-3">
                                <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                                     style={{ color: ETAPA_COLORS[etapa] }}>
                                  {etapa} ({eventos.length})
                                </div>
                                <KanbanCell eventos={eventos} etapa={etapa} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </>
      )}

      {/* Estimador Drawer */}
      {showEstimador && data && (
        <EstimadorDrawer
          eventos={data.eventos}
          paciente={data}
          onClose={() => setShowEstimador(false)}
        />
      )}
    </div>
  );
}
